<script>
	import CustomTable from "src/components/CustomTable/CustomTable.svelte";
	import { getCaseActivities } from "src/services/fmsTasking";
	import { onDestroy, onMount } from "svelte";
	import {
		totalRecords,
		totalPages,
		initPage,
		sizeSelect,
	} from "src/store/store";

	export let caseNo;

	let data = [];
	let allLogs = [];
	let loadingLogs = false;

	$: columns = [
		{
			key: "createdAt",
			label: "Date",
			type: "date",
			format: "DD MMM YYYY, HH:mm",
			className: "table-datetime",
		},
		{
			key: "remark",
			label: "Activity",
			type: "caseActivity",
			caseNo: caseNo,
		},
	];

	onMount(() => {
		initPage.set(1);
	});

	$: if (caseNo) {
		fetchLogs();
	}

	$: {
		const pageSize = parseInt($sizeSelect) || 20;
		const total = Math.ceil(allLogs.length / pageSize);

		totalRecords.set(allLogs.length);
		totalPages.set(total || 1);

		// If current page is out of bounds, reset to 1
		// This handles cases where data length changes or stale initPage exists
		if ($initPage > total && total > 0) {
			initPage.set(1);
		}

		const start = ($initPage - 1) * pageSize;
		const end = start + pageSize;
		data = allLogs.slice(start, end);
	}

	async function fetchLogs() {
		if (!caseNo) return;
		try {
			loadingLogs = true;
			// Fetch a large number of logs to ensure get relevant activities
			const response = await getCaseActivities(caseNo, 1000);
			let rawLogs = response.data?.data || [];

			// group by batch ID and pick the most relevant log per batch
			const batchMap = new Map();

			const createLog = rawLogs.find(
				(l) => l.action === "Create" && l.field === "case_no"
			);
			const creationDate = createLog
				? new Date(createLog.createdAt)
				: null;
			const creationBatch = createLog?.batch;

			rawLogs.forEach((log) => {
				if (!log.batch) return;

				const isCreate =
					log.action === "Create" && log.field === "case_no";
				const isStatusUpdate =
					log.action === "Update" && log.field === "status";
				const isGenericUpdate =
					log.action === "Update" &&
					log.field !== "status" &&
					log.field !== "rejected_reason" &&
					log.field !== "rejected_at" &&
					log.field !== "investigator";

				if (!isCreate && !isStatusUpdate && !isGenericUpdate) return;

				// for those logs before create case_no, only need to log case status changes and not generic updates
				if (isGenericUpdate) {
					if (!createLog) return;
					if (
						(creationDate &&
							new Date(log.createdAt) < creationDate) ||
						(creationBatch && log.batch === creationBatch)
					)
						return;
				}

				// for created case_no and status update, no need to group by batch id
				if (isCreate) {
					batchMap.set(`create-${log.id}`, log);
					return;
				}

				if (isStatusUpdate) {
					batchMap.set(`status-${log.id}`, log);
					return;
				}

				const existing = batchMap.get(log.batch);
				if (!existing) {
					batchMap.set(log.batch, log);
				}
			});

			// sort by newest first and map to required format
			const processedLogs = Array.from(batchMap.values())
				.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
				.map((log) => {
					if (
						log.action === "Update" &&
						log.field === "status" &&
						log.after === "Closed"
					) {
						// find reason log (id - 1) in the original rawLogs
						const reasonLog = rawLogs.find(
							(l) => l.id === log.id - 1
						);
						if (reasonLog) {
							return { ...log, closeReason: reasonLog.after };
						}
					}
					return log;
				});

			allLogs = processedLogs;
		} catch (error) {
			console.error("Error fetching case logs:", error);
		} finally {
			loadingLogs = false;
		}
	}

	// reset pagination stores when component is destroyed
	onDestroy(() => {
		totalRecords.set(0);
		totalPages.set(1);
		initPage.set(1);
	});
</script>

<CustomTable
	{columns}
	rows={data}
	useNavigation={true}
	noHeaders
	borderless
	hover={false}
	emptyMessage="No activities found."
/>
